// Fiche prospect: field groups, labels and small pure helpers shared by its tabs.
import { FileText, Phone, MessageSquareText, Mail, Calendar, Video } from "lucide-react";

// Mirrors backend/prospects/schemas.py ProspectUpdate's allowlist exactly
// (the only fields PATCH /prospects/{id} accepts). Grouped by business
// concept (pharmacie / pharmacien / contact) rather than raw field order, so
// the read view and edit form read like a CRM record, not a flat DB dump.
export const INFO_FIELD_GROUPS = [
  {
    title: "Pharmacie",
    fields: [
      ["pharmacy_name", "Nom de la pharmacie", true],
      ["address", "Adresse"],
      ["region", "Région"],
      ["commune", "Commune"],
      ["phone", "Téléphone principal"],
      ["phone2", "Téléphone secondaire"],
      ["email", "Email pharmacie"],
      ["whatsapp", "WhatsApp"],
      ["acquisition_channel", "Canal d'acquisition", false, "channels"],
    ],
  },
  {
    title: "Pharmacien titulaire",
    fields: [
      ["pharmacist_name", "Nom complet"],
      ["pharmacist_license", "Numéro de licence"],
      ["pharmacist_phone", "Téléphone direct"],
      ["pharmacist_email", "Email"],
    ],
  },
  {
    title: "Contact principal",
    fields: [
      ["reference_name", "Nom"],
      ["reference_function", "Fonction"],
      ["reference_phone", "Téléphone"],
    ],
  },
  {
    title: "Horaires & qualification",
    fields: [
      ["opening_hours", "Horaires d'ouverture"],
      ["on_call_duty", "Gardes"],
      ["closed_days", "Jours de fermeture"],
      ["hours_notes", "Précisions"],
      ["current_software", "Logiciel actuel"],
      ["desired_start", "Démarrage souhaité"],
    ],
  },
];
export const INFO_EDITABLE_KEYS = INFO_FIELD_GROUPS.flatMap((g) => g.fields.map(([k]) => k));

export const INTERACTION_ICONS = {
  note: FileText,
  call: Phone,
  whatsapp: MessageSquareText,
  email: Mail,
  meeting: Calendar,
  demo: Video,
};

export const MIGRATION_STATUS_LABEL = {
  not_started: "Non démarrée",
  in_progress: "En cours",
  completed: "Terminée",
};

// Same 4 milestones for every method, only the wording adapts: the CRM stays
// a generic tracker instead of branching into per-method workflows it doesn't
// itself execute.
const MIGRATION_STEP_KEYS = [
  "step1_source_defined", "step2_data_collected", "step3_data_integrated", "step4_verified",
];

const MIGRATION_STEP_LABELS = {
  default: ["Source / méthode définie", "Données récupérées / inventaire réalisé", "Données intégrées dans Jamora", "Vérification finale"],
  physical_inventory: ["Inventaire planifié", "Inventaire réalisé", "Données intégrées dans Jamora", "Vérification finale"],
  onepharma: ["Migration logicielle planifiée", "Données récupérées", "Données intégrées dans Jamora", "Vérification finale"],
  other_software: ["Migration logicielle planifiée", "Données récupérées", "Données intégrées dans Jamora", "Vérification finale"],
};

export function migrationSteps(method) {
  const labels = MIGRATION_STEP_LABELS[method] || MIGRATION_STEP_LABELS.default;
  return MIGRATION_STEP_KEYS.map((key, i) => [key, labels[i]]);
}

const SUBMISSION_FIELD_LABELS = {
  pharmacy_name: "Nom de la pharmacie",
  address: "Adresse",
  region: "Région",
  commune: "Commune",
  phone: "Téléphone principal",
  phone2: "Téléphone secondaire",
  email: "Email pharmacie",
  whatsapp: "WhatsApp",
  pharmacist_name: "Pharmacien titulaire",
  pharmacist_license: "Numéro de licence",
  pharmacist_phone: "Téléphone du titulaire",
  pharmacist_email: "Email du titulaire",
  reference_name: "Référent",
  reference_function: "Fonction du référent",
  reference_phone: "Téléphone du référent",
  reference_availability: "Disponibilité du référent",
  reference_timeslot: "Plage horaire préférée",
  opening_hours: "Horaires d'ouverture",
  on_call_duty: "Gardes",
  closed_days: "Jours de fermeture",
  hours_notes: "Précisions horaires",
  current_software: "Logiciel actuel",
  stock_method: "Méthode de gestion du stock",
  medicines_list: "Liste de médicaments disponible",
  medicines_list_format: "Format de la liste",
  has_scanner: "Scanner code-barres",
  jamora_device: "Appareil prévu pour Jamora",
  reference_count: "Nombre de références",
  wholesalers: "Fournisseurs grossistes",
  other_suppliers: "Autres fournisseurs",
  product_types: "Types de produits vendus",
  cold_chain: "Médicaments chaîne du froid",
  physical_inventory_available: "Inventaire physique possible",
  sales_tracking_during_inventory: "Suivi des ventes pendant inventaire",
  migration_needs: "Besoins de migration",
  data_source: "Source de données",
  wanted_features: "Fonctionnalités souhaitées",
  computer_level: "Niveau informatique",
  digital_comfort: "Aisance numérique de l'équipe",
  planned_users: "Utilisateurs prévus",
  questions: "Questions / remarques",
  desired_start_delay: "Délai souhaité de démarrage",
  mode_preference: "Préférence d'onboarding",
  internet_quality: "Connexion internet",
};

export function submissionFieldLabel(key) {
  return SUBMISSION_FIELD_LABELS[key] || key.replace(/_/g, " ");
}

function dayLabel(iso) {
  const date = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === now.toDateString()) return "Aujourd'hui";
  if (date.toDateString() === yesterday.toDateString()) return "Hier";
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
}

export function groupByDay(entries) {
  const groups = [];
  for (const e of entries) {
    const label = dayLabel(e.occurred_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.entries.push(e);
    else groups.push({ label, entries: [e] });
  }
  return groups;
}
